import type { DocSyncRequestSource } from '@affine/nbstore';
import { DatabasePublicationBaseUnavailable } from '@blocksuite/affine/blocks/database';
import type { Workspace } from '@blocksuite/affine/store';
import { DatabaseBlockModel } from '@blocksuite/affine-model';
import {
  DATABASE_ROW_SHARD_ROWS_MAP_KEY,
  readDatabaseShardDirectoryPartDocIds,
} from '@streamlain/database-identity';

import {
  collectDatabaseSystemDocAccessBindingsFromPage,
  type DatabaseSystemDocAccessBinding,
} from './database-subdoc-preload';

export type { DatabaseSystemDocAccessBinding };

/** An already retained trash identity, resolved from its local root manifest. */
export interface ResolvedDatabaseSystemDocBinding {
  readonly databaseId: string;
  readonly hostDocId: string;
  readonly rootDocId: string;
  readonly manifest: DatabaseSystemDocAccessBinding['manifest'];
  readonly blockId: string;
}

type DatabaseSystemDocSyncEngine = {
  addDoc?: (docId: string, source: DocSyncRequestSource) => void;
  addPriority?: (docId: string, priority: number) => () => void;
  flushAndWaitForSynced?: (docId: string, abort?: AbortSignal) => Promise<void>;
};

/** A document did not reach the server before a durable write boundary. */
export class DurableWriteSyncError extends Error {
  override readonly name = 'DurableWriteSyncError';

  constructor(
    readonly docId: string,
    cause: unknown
  ) {
    const detail =
      cause instanceof Error ? cause.message : 'unknown sync failure';
    super(`Document "${docId}" sync failed: ${detail}`, { cause });
  }
}

const DURABLE_WRITE_SYNC_PRIORITY = 1_000;
// A foreground write can overlap the initial root/shard preload after
// navigation. That preload deliberately has a finite 30-second window, so the
// stricter durable-write boundary must allow the same window while the shared
// sync queue is still making progress.
const DURABLE_WRITE_SYNC_TIMEOUT_MS = 30_000;

/**
 * Whether a database system document holds content in this client. A
 * deterministic row-shard id can name a document that exists nowhere yet;
 * such a phantom has nothing to sync and a wait on it never ends.
 */
export function hasMaterializedLocalDatabaseDoc(
  collection: Workspace,
  docId: string
): boolean {
  const doc = collection.getDoc(docId);
  if (!doc) return false;
  if (!doc.ready) {
    doc.load();
  }
  const store = doc.getStore({ id: docId });
  return (
    store.doc.yBlocks.size > 0 ||
    store.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY).size > 0
  );
}

export async function flushDatabaseSystemDocsForDurableWrite(input: {
  collection: Workspace;
  docEngine: DatabaseSystemDocSyncEngine;
  docId: string;
  workspaceId: string;
  workspaceFirst?: boolean;
  databaseIds?: readonly string[];
  /** Selected live hosts use the existing host/root discovery path. */
  liveBindings?: readonly {
    hostDocId: string;
    databaseIds: readonly string[];
  }[];
  resolvedBindings?: readonly ResolvedDatabaseSystemDocBinding[];
  includeParentDocs?: boolean;
}): Promise<readonly DatabaseSystemDocAccessBinding[]> {
  const resolveStore = (docId: string) => {
    const doc = input.collection.getDoc(docId);
    if (!doc) return null;
    if (!doc.ready) doc.load();
    return doc.getStore({ id: docId });
  };
  const bindingsByIdentity = new Map<string, DatabaseSystemDocAccessBinding>();
  const addBinding = (binding: DatabaseSystemDocAccessBinding) => {
    bindingsByIdentity.set(
      JSON.stringify([
        binding.accessDocId,
        binding.databaseId,
        binding.rootDocId,
      ]),
      binding
    );
  };
  for (const source of input.liveBindings ?? [
    { hostDocId: input.docId, databaseIds: input.databaseIds },
  ]) {
    const page = resolveStore(source.hostDocId);
    if (!page) throw new DatabasePublicationBaseUnavailable();
    const requested = source.databaseIds
      ? new Set(source.databaseIds)
      : undefined;
    const discovered = collectDatabaseSystemDocAccessBindingsFromPage(
      page,
      resolveStore,
      requested
    );
    if (
      requested &&
      [...requested].some(
        id => !discovered.some(binding => binding.databaseId === id)
      )
    )
      throw new DatabasePublicationBaseUnavailable();
    for (const binding of discovered) addBinding(binding);
  }
  for (const binding of input.resolvedBindings ?? []) {
    const root = resolveStore(binding.rootDocId);
    // Explicit identities must have their local root; never flush a phantom DB.
    if (
      !root
        ?.getBlocksByFlavour('affine:database')
        .some(
          block =>
            block.model instanceof DatabaseBlockModel &&
            (block.model.id === binding.databaseId ||
              block.model.props.databaseId === binding.databaseId)
        )
    )
      throw new DatabasePublicationBaseUnavailable();
    addBinding({
      databaseId: binding.databaseId,
      rootDocId: binding.rootDocId,
      accessDocId: binding.hostDocId,
      blockId: binding.blockId,
      manifest: binding.manifest,
    });
  }
  const bindings = [...bindingsByIdentity.values()];
  if (!input.docEngine.flushAndWaitForSynced) {
    throw new Error('Document sync engine is not available.');
  }
  // DocFrontend implements this as a prototype method that relies on `this`.
  const flushAndWaitForSynced = input.docEngine.flushAndWaitForSynced.bind(
    input.docEngine
  );

  // The head on each root names its directory parts; the server reads them
  // together with the root.
  const directoryPartDocIds = (binding: DatabaseSystemDocAccessBinding) => {
    const model = resolveStore(binding.rootDocId)
      ?.getBlocksByFlavour('affine:database')
      .map(block => block.model)
      .find(
        model =>
          model instanceof DatabaseBlockModel &&
          (model.id === binding.databaseId ||
            model.props.databaseId === binding.databaseId)
      );
    if (!(model instanceof DatabaseBlockModel))
      throw new DatabasePublicationBaseUnavailable();
    return readDatabaseShardDirectoryPartDocIds(model);
  };
  const localSystemDocIds = bindings.flatMap(binding => [
    binding.rootDocId,
    ...directoryPartDocIds(binding),
    ...binding.manifest.shards
      .map(shard => shard.serverDocId)
      // A new empty sharded database has a deterministic default shard in its
      // access manifest before that row-shard document exists. Never wait for
      // sync on a phantom doc; the binding still carries the complete
      // deterministic manifest for later rows.
      .filter(docId =>
        hasMaterializedLocalDatabaseDoc(input.collection, docId)
      ),
  ]);
  const includeParentDocs = input.includeParentDocs !== false;
  const docIds = new Set([
    ...(includeParentDocs ? [input.workspaceId, input.docId] : []),
    ...localSystemDocIds,
  ]);
  const releasePriorities: Array<() => void> = [];
  for (const docId of docIds) {
    input.docEngine.addDoc?.(docId, 'permission-mutation-preflight');
    const releasePriority = input.docEngine.addPriority?.(
      docId,
      DURABLE_WRITE_SYNC_PRIORITY
    );
    if (releasePriority) {
      releasePriorities.push(releasePriority);
    }
  }
  const flushDoc = async (docId: string) => {
    const startedAt =
      typeof performance !== 'undefined' ? performance.now() : null;
    try {
      await flushAndWaitForSynced(
        docId,
        AbortSignal.timeout(DURABLE_WRITE_SYNC_TIMEOUT_MS)
      );
    } catch (error) {
      throw new DurableWriteSyncError(docId, error);
    } finally {
      if (startedAt !== null && typeof performance.measure === 'function') {
        performance.measure(`app-base-durable-sync:${docId}`, {
          start: startedAt,
          end: performance.now(),
        });
      }
    }
  };
  // Server mutations involving newly created docs require the parent-first
  // boundary. Existing database content writes can skip that unchanged parent
  // roundtrip and only need the final parent-after-children acknowledgement.
  try {
    if (includeParentDocs && input.workspaceFirst !== false) {
      await flushDoc(input.workspaceId);
    }
    await Promise.all(
      [...docIds]
        .filter(docId => docId !== input.workspaceId)
        .map(docId => flushDoc(docId))
    );
    if (includeParentDocs) {
      await flushDoc(input.workspaceId);
    }
  } finally {
    releasePriorities.forEach(release => release());
  }
  return bindings;
}

/** Flushes the page and its database documents before a server mutation reads them. */
export async function prepareDatabaseSystemDocsForServerMutation(input: {
  collection: Workspace;
  docEngine: DatabaseSystemDocSyncEngine;
  docId: string;
  workspaceId: string;
}): Promise<void> {
  await flushDatabaseSystemDocsForDurableWrite(input);
}
